/**
 * EnergyControlCheckoutButton — Energy Control promo checkout.
 *
 * Routes to POST /api/subscriptions/monitoring/checkout — Stripe Checkout
 * in subscription mode op de intro-Price (€ 9,95 / maand · 3 maanden).
 * De automatische overgang naar de reguliere prijs van € 14,95 / maand
 * wordt server-side afgehandeld via een Stripe Subscription Schedule
 * (services/monitoring_promo_schedule.py).
 *
 * Behaviour:
 *  - When `enabled === false` and `showDisabled === true`: renders a
 *    disabled "Binnenkort beschikbaar" button. Otherwise renders nothing.
 *  - When `enabled === true`: renders the active CTA. On click, POSTs
 *    and redirects to `data.url` (Stripe checkout). On HTTP 503 shows
 *    "Deze optie is nog niet actief." On other errors shows a generic
 *    failure toast/alert. Never navigates to a black screen.
 */
import { useState } from 'react';
import { Loader2, ShieldCheck } from 'lucide-react';
import { trackFunnelEvent, getAttribution } from '@/utils/tracking';

export default function EnergyControlCheckoutButton({
  enabled,
  email = null,
  leadId = null,
  intakeId = null,
  refCode = null,
  label = 'Start Energy Control · € 9,95 eerste 3 mnd · daarna € 14,95',
  showDisabled = false,
  className = '',
}) {
  const [loading, setLoading] = useState(false);
  const [errorMsg, setErrorMsg] = useState('');

  const onClick = async () => {
    if (loading) return;
    trackFunnelEvent('payment_clicked', { plan: 'monitoring_monthly_intro' });
    setErrorMsg('');
    setLoading(true);
    try {
      // P0 Fix 1 — geef gclid/utm/landing/referrer mee door naar Stripe
      // zodat payment_completed herleidbaar blijft tot de oorspronkelijke
      // Ads-klik. Lege strings ipv null houdt het backend-schema simpel.
      const attr = getAttribution() || {};
      const sessionId =
        (typeof window !== 'undefined' && sessionStorage.getItem('powersure_session')) || null;
      const res = await fetch('/api/subscriptions/monitoring/checkout', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email: email || null,
          lead_id: leadId || null,
          intake_id: intakeId || null,
          ref_code: refCode || null,
          session_id: sessionId,
          // Attributie — alleen waarden meesturen, geen PII
          gclid: attr.gclid || null,
          gbraid: attr.gbraid || null,
          wbraid: attr.wbraid || null,
          utm_source: attr.utm_source || null,
          utm_medium: attr.utm_medium || null,
          utm_campaign: attr.utm_campaign || null,
          utm_term: attr.utm_term || null,
          utm_content: attr.utm_content || null,
          landing_url: attr.landing_url || null,
          referrer: attr.referrer || null,
        }),
      });
      if (res.status === 503) {
        setErrorMsg('Deze optie is nog niet actief.');
        return;
      }
      if (!res.ok) {
        setErrorMsg('Checkout kon niet worden gestart. Probeer het later opnieuw.');
        return;
      }
      const data = await res.json();
      if (!data?.url) {
        setErrorMsg('Checkout-URL ontbreekt. Probeer het later opnieuw.');
        return;
      }
      trackFunnelEvent('checkout_started', { plan: 'monitoring_monthly_intro', amount: data.amount });
      if (typeof window !== 'undefined' && window.pwTrack) {
        window.pwTrack('ec_intro_checkout_start', { amount: data.amount });
      }
      window.location.href = data.url;
    } catch (err) {
      setErrorMsg('Netwerkfout. Probeer het later opnieuw.');
    } finally {
      setLoading(false);
    }
  };

  if (!enabled) {
    if (!showDisabled) return null;
    return (
      <button
        type="button"
        disabled
        className={[
          'inline-flex items-center justify-center gap-1.5 px-4 py-2.5 rounded-md text-sm font-semibold',
          'border-2 border-muted bg-muted/30 text-muted-foreground cursor-not-allowed opacity-70',
          className,
        ].join(' ')}
        data-testid="ec-v2-checkout-button-disabled"
        data-fase1-flag="EC_V2_CHECKOUT_ENABLED"
      >
        <ShieldCheck className="w-3.5 h-3.5" />
        Binnenkort beschikbaar
      </button>
    );
  }

  return (
    <div className="flex flex-col gap-1.5" data-testid="ec-v2-checkout-button-wrap">
      <button
        type="button"
        onClick={onClick}
        disabled={loading}
        className={[
          'btn-primary inline-flex items-center justify-center gap-1.5 px-4 py-2.5 rounded-md text-sm font-semibold disabled:opacity-60',
          className,
        ].join(' ')}
        data-testid="ec-v2-checkout-button"
        data-fase1-flag="EC_V2_CHECKOUT_ENABLED"
      >
        {loading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <ShieldCheck className="w-3.5 h-3.5" />}
        {loading ? 'Bezig…' : label}
      </button>
      {errorMsg && (
        <p
          className="text-[12px] text-rose-700 bg-rose-50 border border-rose-200 rounded-md px-2.5 py-1.5"
          data-testid="ec-v2-checkout-error"
        >
          {errorMsg}
        </p>
      )}
    </div>
  );
}
